import React, { useEffect, useState } from 'react';
import vaultIcon from '@/assets/icons/vault-dark.svg';
import binIcon from '@/assets/icons/bin.svg';
import editIcon from '@/assets/icons/edit.svg';
import infoIcon from '@/assets/icons/info.svg';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import percentageIcon from '@/assets/icons/percentage.svg';
import euroIcon from '@/assets/icons/euro.svg';
import invoiceIcon from '@/assets/icons/invoice.svg';
import { Button } from '@/components/ui/button';
import noInvoiceIcon from '@/assets/icons/no-invoice.svg';
import { setBreadcrumbs } from '@/store/slices/breadcrumb';
import { PAGE } from '@/routes';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { bankAccountService } from '@/services/bank-accounts.service';
import { BankAccount } from '@/services/types/bank-accounts.types';
import InfoModal from '@/components/molecules/InfoModal';
import deleteBankAccountIcon from '@/assets/icons/delete-bankAccount.svg';
import toast from 'react-hot-toast';
import ExplanatoryModal from '@/components/molecules/ExplanatoryModal';

import baExpanatoryImg from '@/assets/images/ba-explanatory-1.png';
import baExpanatoryImg2 from '@/assets/images/ba-explanatory-2.png';

const steps = [
  {
    title: 'What Is a Bank Account?',
    message:
      'Bank accounts act as the primary source for payments and deposits, seamlessly integrating with your budget.',
    img: baExpanatoryImg
  },
  {
    title: 'Tracking Invoices in Your Bank Account',
    message:
      'All invoices are linked to bank accounts, enabling tracking of cash inflows and outflows while maintaining account balances.',
    img: baExpanatoryImg2
  }
];

const BankAccountDetailPage = () => {
  const [bankAccountMode, setBankAccountMode] = useState('euro');
  const [bankAccount, setBankAccount] = useState<BankAccount>();
  const [loadingBankAccount, setLoadingBankAccount] = useState(false);
  const [deleteConfirmModal, setDeleteConfirmModal] = useState(false);
  const [isExplanatoryOpen, setIsExplanatoryOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);

  const { budgetId = '', bankAccountId = '' } = useParams();

  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  useEffect(() => {
    // Sets breadcrumbs when component mounts
    dispatch(
      setBreadcrumbs([
        {
          title: 'Budgets',
          path: PAGE.BUDGETS.path
        },
        {
          title: 'Bank Accounts Overview',
          path: PAGE.BANK_ACCOUNTS.path.replace(':budgetId', budgetId || '')
        },
        {
          title: 'Bank Account',
          path: PAGE.BANK_ACCOUNTS.children.DETAIL
        }
      ])
    );

    fetchBankAccountDetail();
  }, [budgetId, bankAccountId, dispatch, location.pathname]);

  const fetchBankAccountDetail = async () => {
    try {
      setLoadingBankAccount(true);
      const { data: bankAccountData } = await bankAccountService.getBankAccount(budgetId, bankAccountId);
      setBankAccount(bankAccountData);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingBankAccount(false);
    }
  };

  const handleEditClick = () => {
    if (budgetId) {
      navigate(`/budgets/${budgetId}/bank-accounts/${bankAccountId}/edit`);
    }
  };

  const handleDeleteClick = () => {
    setDeleteConfirmModal(true);
  };

  const handleDeleteBankAccount = async () => {
    await bankAccountService.deleteBankAccount(budgetId, bankAccountId);

    toast.success('Budget deleted successfully');
    navigate(`/budgets/${budgetId}/bank-accounts`);
  };

  return (
    <div className="h-full flex flex-col items-stretch gap-4">
      <div className="flex items-stretch gap-4">
        <div className="flex-[2] border bg-white rounded-[16px] p-4">
          <header className="flex items-center justify-between gap-4 mb-4">
            <h3 className="flex items-center gap-[0.6rem]">
              {' '}
              <img src={vaultIcon} alt="vault icon" />{' '}
              <span className="font-semibold text-2xl"> {bankAccount?.accountName || '-'}</span>
            </h3>
            <div className="flex items-center gap-3">
              <button onClick={handleEditClick} title="Edit">
                <img className="w-[28px] h-[28px]" src={editIcon} alt="vault icon" />
              </button>
              <button
                title="Info"
                onClick={() => {
                  setCurrentStep(0);
                  setIsExplanatoryOpen(true);
                }}
              >
                <img className="w-[28px] h-[28px]" src={infoIcon} alt="vault icon" />
              </button>
              <button onClick={handleDeleteClick} title="Delete">
                <img className="w-[28px] h-[28px]" src={binIcon} alt="vault icon" />
              </button>
            </div>
          </header>
          <div className="bg-lightPurple p-4 rounded-[16px] border border-primary">
            <p className="font-bold text-primary mb-3">General Information</p>
            {loadingBankAccount ? (
              <span className="block w-full text-secondary text-center py-8">Loading...</span>
            ) : (
              <>
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <p className="text-sm text-secondary">Name of the bank</p>
                    <span className="font-medium">{bankAccount?.bankName || '-'}</span>
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-secondary">Country of the bank</p>
                    <span className="font-medium">{bankAccount?.bankCountry || '-'}</span>
                  </div>
                </div>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <p className="text-sm text-secondary">Bank Account Number</p>
                    <span className="font-medium">{bankAccount?.accountNumber || '-'}</span>
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-secondary">Initial Amount</p>
                    <span className="font-medium">€{bankAccount?.initialAmount || '-'}</span>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
        <div className="flex-1 w-full flex flex-col items-stretch min-w-[418px] border bg-white rounded-[16px] p-4">
          <header className="flex items-center justify-between">
            <p className="font-bold">Balance</p>
            <Tabs defaultValue="percentage" value={bankAccountMode} onValueChange={setBankAccountMode}>
              <TabsList className="bg-lightPurple-200 h-auto py-[0.2rem]">
                <TabsTrigger value="percentage" className="group md:py-[0.3rem]">
                  <img
                    src={percentageIcon}
                    alt="percentage-icon"
                    width={13}
                    height={17}
                    className="group-data-[state=active]:brightness-[100]"
                  />
                </TabsTrigger>
                <TabsTrigger value="euro" className="group md:py-[0.3rem]">
                  <img
                    src={euroIcon}
                    alt="euro-icon"
                    width={12}
                    height={12}
                    className="w-[12px] h-[12px] group-data-[state=active]:brightness-[100]"
                  />
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </header>
          <div className="flex-1 flex flex-col items-center justify-center py-8">
            <p className="text-primary text-[3rem] font-bold leading-[135%]">
              {bankAccountMode === 'euro'
                ? `€${bankAccount?.remainingBalance || 'N/A'}`
                : `${
                    bankAccount?.initialAmount
                      ? Math.round(((bankAccount?.remainingBalance || 0) / bankAccount.initialAmount) * 100)
                      : 'N/A'
                  }%`}
            </p>
            <span className="text-secondary text-[1.25rem] ">
              {bankAccountMode === 'euro'
                ? `${((bankAccount?.remainingBalance || 0) - (bankAccount?.initialAmount || 0)).toLocaleString('en-US')}€ from Initial amount`
                : `${
                    bankAccount?.initialAmount
                      ? Math.round(
                          (((bankAccount?.remainingBalance || 0) - bankAccount.initialAmount) /
                            bankAccount.initialAmount) *
                            100
                        )
                      : 0
                  }% from Initial amount`}
            </span>
          </div>
        </div>
      </div>
      <div className="flex-1 flex flex-col items-stretch border bg-white rounded-[16px] p-4">
        <h5 className="flex items-center gap-2">
          <img src={invoiceIcon} alt="" />
          <span className="font-bold">Recent Invoice</span>
        </h5>
        <div className="flex-1 flex items-center justify-center flex-col gap-4">
          <div className="flex items-center gap-2">
            <img className="w-[40px] h-[40px]" src={noInvoiceIcon} alt="no-invoice-icon" />
            <div className="flex flex-col items-start justify-center">
              <p className="font-medium leading-[140%]">There are no Invoice to show</p>
              <span className="text-secondary text-sm leading-[125%]">
                Please create a new invoice to allocate budget!
              </span>
            </div>
          </div>
          <Button variant={'primary-reverse'} className="rounded-full px-6">
            Create an Invoice
          </Button>
        </div>
      </div>

      {/* Delete Confirm Modal */}
      {deleteConfirmModal && (
        <InfoModal
          isOpen={deleteConfirmModal}
          variant="confirm"
          title="Are you sure want to Delete this Bank Account?"
          message={<p>Deleted bank account will removed permanently and unable to restore</p>}
          buttonText="Delete"
          onButtonClick={handleDeleteBankAccount}
          onClose={() => setDeleteConfirmModal(false)}
          icon={deleteBankAccountIcon}
        />
      )}

      {/* Explanatory Modal */}
      {isExplanatoryOpen && (
        <ExplanatoryModal
          isOpen={isExplanatoryOpen}
          steps={steps}
          currentStep={currentStep}
          buttonText="Next"
          onButtonClick={() => {
            setCurrentStep(currentStep + 1);
          }}
          onClose={() => {
            setIsExplanatoryOpen(false);
          }}
        />
      )}
    </div>
  );
};

export default BankAccountDetailPage;
